import React, { useEffect, useState, useRef, Component } from 'react';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $45)
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = ['https://cdn.magicpatterns.com/uploads/ptMERoj5LvFP4KvLCMA7zo/file_000000003af8720c9523ee9da7df0e47.png', 'https://cdn.magicpatterns.com/uploads/v6Xgr2WKEWwEzmbTCFpot9/253_gravel_tacoma_premier_gravel_supplier.png', 'https://cdn.magicpatterns.com/uploads/6vA9AJYsUSqFK7vnpypgyq/gravel_delivery_tacoma.jpg', 'https://cdn.magicpatterns.com/uploads/nwPGL1Z6ypL7t6q3URXKRS/gravel_landscaping_in_tacoma.png'];
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneInfo(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    fee: 40,
    label: '+$40 Delivery'
  };
  if (miles <= 13) return {
    zone: 2,
    fee: 75,
    label: '+$75 Delivery'
  };
  if (miles <= 18) return {
    zone: 3,
    fee: 120,
    label: '+$120 Delivery'
  };
  if (miles <= 22) return {
    zone: 4,
    fee: 150,
    label: '+$150 Delivery'
  };
  return {
    zone: null,
    fee: 0,
    label: 'Custom Quote'
  };
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-420">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between ${value ? 'text-white' : 'text-white/50'}`} data-id="element-421">
        <span className="truncate" data-id="element-422">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-423" />
      </button>

      <AnimatePresence data-id="element-424">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full mt-2 bg-stone-900 border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-425">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-white/30 cursor-not-allowed bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-white hover:bg-white/10'}`} data-id="element-426">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('Base & Compaction');
  const [quantity, setQuantity] = useState('');
  const [numLoads, setNumLoads] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
  } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    try {
      const key = 'hero_image_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % heroImages.length;
      sessionStorage.setItem(key, String(index + 1));
      return heroImages[index];
    } catch {
      return heroImages[0];
    }
  });
  const [showEstimator, setShowEstimator] = useState(false);
  const [showAddressRow, setShowAddressRow] = useState(false);
  const [hasInteractedWithMaterial, setHasInteractedWithMaterial] = useState(true);
  const [hasInteractedWithQuantity, setHasInteractedWithQuantity] = useState(false);
  // Auto-reveal address row once user has interacted with both fields
  useEffect(() => {
    if (hasInteractedWithMaterial && hasInteractedWithQuantity) {
      if (quantity === 'multi' && !numLoads) {
        setShowAddressRow(false);
        return;
      }
      const timer = setTimeout(() => setShowAddressRow(true), 300);
      return () => clearTimeout(timer);
    }
  }, [hasInteractedWithMaterial, hasInteractedWithQuantity, quantity, numLoads]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) return;
    setIsSearching(true);
    setQuoteResult(null);
    try {
      // Geocode the address
      const query = encodeURIComponent(address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
        zoneInfo = getZoneInfo(distance);
      }
      const priceRange = categoryPriceMap[material] || {
        min: 55,
        max: 55
      };
      const isBark = material === 'Bark';
      const fullLoadYards = isBark ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (isBark) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      const effectiveLow = Math.ceil(priceRange.min * surchargeMultiplier);
      const effectiveHigh = Math.ceil(priceRange.max * surchargeMultiplier);
      const totalLow = effectiveLow * qty + zoneInfo.fee * deliveries;
      const totalHigh = effectiveHigh * qty + zoneInfo.fee * deliveries;
      setQuoteResult({
        priceLow: totalLow,
        priceHigh: totalHigh,
        address,
        quantity: quantity === 'multi' ? String(qty) : quantity,
        material,
        deliveryZone: zoneInfo.zone,
        deliveryFee: zoneInfo.fee * deliveries,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effectiveLow,
        effectivePricePerYardHigh: effectiveHigh,
        basePricePerYardLow: priceRange.min,
        basePricePerYardHigh: priceRange.max,
        deliveries
      });
    } catch {
      const priceRange = categoryPriceMap[material] || {
        min: 55,
        max: 55
      };
      const isBark = material === 'Bark';
      const fullLoadYards = isBark ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (isBark) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      const effectiveLow = Math.ceil(priceRange.min * surchargeMultiplier);
      const effectiveHigh = Math.ceil(priceRange.max * surchargeMultiplier);
      const totalLow = effectiveLow * qty + 100 * deliveries;
      const totalHigh = effectiveHigh * qty + 100 * deliveries;
      setQuoteResult({
        priceLow: totalLow,
        priceHigh: totalHigh,
        address,
        quantity: quantity === 'multi' ? String(qty) : quantity,
        material,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effectiveLow,
        effectivePricePerYardHigh: effectiveHigh,
        basePricePerYardLow: priceRange.min,
        basePricePerYardHigh: priceRange.max,
        deliveries
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowAddressRow(false);
    setMaterial('Base & Compaction');
    setQuantity('');
    setNumLoads('');
    setHasInteractedWithMaterial(true);
    setHasInteractedWithQuantity(false);
    setQuoteResult(null);
    setAddress('');
  };
  return <section className="relative w-full bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-427">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-428" />
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/80 via-black/50 to-black/40" data-id="element-429" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-430">
        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-431">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="flex items-center justify-center md:justify-start gap-2 mb-4 md:mb-6" data-id="element-432">
            <div className="flex text-[#b8872a]" data-id="element-433">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-current" data-id="element-434" />)}
            </div>
            <span className="text-white/90 text-sm sm:text-base font-medium" data-id="element-435">
              Trusted by the 253 and beyond
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} data-id="element-436">
            <div className="w-12 md:w-16 h-1 md:h-1.5 bg-brand-orange mb-4 md:mb-6 mx-auto md:mx-0 rounded-full" data-id="element-437" />
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 text-white leading-[1.1]" data-id="element-438">
              Local Gravel Delivery,
              <span className="text-brand-orange block mt-1 md:mt-2" data-id="element-439">
                Simplified.
              </span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/80 font-semibold font-sub mb-6 md:mb-10 max-w-2xl mx-auto md:mx-0" data-id="element-440">
            Get an instant quote for crushed rock, gravel and other landscaping
            materials delivered to your driveway or jobsite.
          </motion.p>

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-441">
              <button onClick={() => {
            const el = document.getElementById('materials');
            if (el) {
              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} className="px-6 py-5 bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white hover:text-stone-900 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center" data-id="element-442">
                Explore Our Catalog
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-443" />
              </button>
              <button onClick={() => {
            setShowEstimator(true);
            setTimeout(() => {
              if (estimatorRef.current) {
                const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight + 8;
                window.scrollTo({
                  top,
                  behavior: 'smooth'
                });
              }
            }, 150);
          }} className="px-6 py-5 bg-brand-button hover:bg-brand-buttonHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors flex items-center justify-center shadow-lg shadow-brand-orange/20" data-id="element-444">
                Get Instant Estimate
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-445" />
              </button>
            </motion.div>}
        </div>

        {/* Estimator Card */}
        <AnimatePresence data-id="element-446">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className="relative z-20 bg-black/40 backdrop-blur-xl rounded-2xl border border-white/20 p-6 sm:p-8 mb-10 max-w-3xl mx-auto md:mx-0 shadow-2xl scroll-mt-[125px]" data-id="element-447">
              <button onClick={handleCloseEstimator} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors" data-id="element-448">
                <X className="w-5 h-5" data-id="element-449" />
              </button>

              <p className="text-sm font-bold text-brand-muted uppercase tracking-widest mb-5 flex items-center" data-id="element-450">
                <span className="w-8 h-px bg-brand-muted mr-3" data-id="element-451"></span>
                Get an Instant Estimate
              </p>

              <form onSubmit={handleSearch} className="space-y-4" data-id="element-452">
                {/* Row 1: Material + Quantity */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-453">
                  <CustomSelect value={material} onChange={val => {
                setMaterial(val);
                setHasInteractedWithMaterial(true);
              }} placeholder="Select Material" options={[{
                value: 'Base & Compaction',
                label: 'Base & Compaction'
              }, {
                value: 'Clean & Drain Rock',
                label: 'Clean & Drain Rock'
              }, {
                value: 'Bark',
                label: 'Bark'
              }, {
                value: 'Soil, Mulch & Org.',
                label: 'Soil & Topsoil'
              }, {
                value: 'Landscape & Decor',
                label: 'Landscape & Decor'
              }, {
                value: 'Quarry Rock',
                label: 'Quarry Rock'
              }]} data-id="element-454" />
                  <CustomSelect value={quantity} onChange={val => {
                setQuantity(val);
                setHasInteractedWithQuantity(true);
                if (val !== 'multi') setNumLoads('');
              }} placeholder="Quantity" options={material === 'Bark' ? [{
                value: '7',
                label: '7 Yards'
              }, {
                value: '8',
                label: '8 Yards'
              }, {
                value: '9',
                label: '9 Yards'
              }, {
                value: '10',
                label: '10 Yards'
              }, {
                value: '11',
                label: '11 Yards'
              }, {
                value: '12',
                label: '12 Yards'
              }, {
                value: '13',
                label: '13 Yards'
              }, {
                value: '14',
                label: '14 Yards'
              }, {
                value: '15',
                label: '15 Yards (Full Load)'
              }, {
                value: 'multi',
                label: 'More than 1 load'
              }] : [{
                value: '6',
                label: '6 Yards (Half Load)'
              }, {
                value: '7',
                label: '7 Yards'
              }, {
                value: '8',
                label: '8 Yards'
              }, {
                value: '9',
                label: '9 Yards'
              }, {
                value: '10',
                label: '10 Yards'
              }, {
                value: '11',
                label: '11 Yards'
              }, {
                value: '12',
                label: '12 Yards (Full Load)'
              }, {
                value: 'multi',
                label: 'More than 1 load'
              }]} data-id="element-455" />
                </motion.div>

                <AnimatePresence data-id="element-456">
                  {quantity === 'multi' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-visible" data-id="element-457">
                      <div data-id="element-458">
                        <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                    value: '2',
                    label: '2 Loads'
                  }, {
                    value: '3',
                    label: '3 Loads'
                  }, {
                    value: '3+',
                    label: '3+ Loads (Custom Quote)'
                  }]} data-id="element-459" />
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg mt-3" data-id="element-460">
                        <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-461" />
                        <p className="text-xs text-white/90" data-id="element-462">
                          Multi-load orders require separate deliveries. You
                          will be charged a delivery fee per load.
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Row 2: Address + Button */}
                <AnimatePresence data-id="element-463">
                  {showAddressRow && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="flex flex-col sm:flex-row gap-3 overflow-hidden" data-id="element-464">
                      <div className="relative flex-grow" data-id="element-465">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-466">
                          <MapPin className="h-4 w-4 text-white/60" data-id="element-467" />
                        </div>
                        <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter delivery address" autoComplete="street-address" className="block w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required data-id="element-468" />
                      </div>
                      <button type="submit" disabled={isSearching} className="bg-brand-button hover:bg-brand-buttonHover text-white px-6 py-3 rounded-lg font-bold text-sm transition-colors flex items-center justify-center sm:w-auto w-full disabled:opacity-70 shrink-0" data-id="element-469">
                        {isSearching ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-470"></div> : <>
                            Get Estimate
                            <ArrowRight className="w-4 h-4 ml-2" data-id="element-471" />
                          </>}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex items-center justify-center text-xs text-white/60" data-id="element-472">
                <Lock className="w-3 h-3 mr-1.5 opacity-70" data-id="element-473" />
                Your information is secure
              </div>

              {/* Quote Result */}
              {quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-white/10" data-id="element-474">
                  <p className="text-xs text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-475">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-white/90 font-medium mb-4" data-id="element-476">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-477">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Product names in category */}
                  {(() => {
              const mat = quoteResult.material;
              let productNames: string[] = [];
              if (mat === 'Bark') {
                productNames = categories.flatMap(c => c.products).filter(p => p.name.toLowerCase().includes('bark')).map(p => p.name);
              } else {
                const cat = categories.find(c => c.name === mat);
                if (cat) {
                  productNames = cat.products.map(p => p.name);
                }
              }
              if (productNames.length > 0) {
                return <p className="text-xs text-white/50 mb-4 -mt-2" data-id="element-478">
                          Includes: {productNames.join(' · ')}
                        </p>;
              }
              return null;
            })()}

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-479">
                    <div className="flex justify-between text-sm" data-id="element-480">
                      <span className="text-white/70" data-id="element-481">
                        Material —{' '}
                        {quoteResult.effectivePricePerYardLow === quoteResult.effectivePricePerYardHigh ? `${quoteResult.effectivePricePerYardLow}/yd` : `${quoteResult.effectivePricePerYardLow}–${quoteResult.effectivePricePerYardHigh}/yd`}{' '}
                        × {quoteResult.quantity} yds
                      </span>
                      <span className="text-white font-semibold" data-id="element-482">
                        {quoteResult.effectivePricePerYardLow === quoteResult.effectivePricePerYardHigh ? `${quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}` : `${quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}–${quoteResult.effectivePricePerYardHigh * parseInt(quoteResult.quantity)}`}
                      </span>
                    </div>
                    {quoteResult.smallLoadSurcharge > 0 && <p className="text-xs text-brand-orange -mt-1 ml-0.5" data-id="element-483">
                        Includes small load upcharge (
                        {quoteResult.basePricePerYardLow === quoteResult.basePricePerYardHigh ? `${quoteResult.basePricePerYardLow}/yd` : `${quoteResult.basePricePerYardLow}–${quoteResult.basePricePerYardHigh}/yd`}{' '}
                        at 10+ yards)
                      </p>}
                    <div className="flex justify-between text-sm" data-id="element-484">
                      <span className="text-white/70" data-id="element-485">
                        Delivery —{' '}
                        {quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className="text-white font-semibold" data-id="element-486">
                        {quoteResult.deliveryZone !== null ? `${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10" data-id="element-487">
                      <span className="text-white font-bold text-sm" data-id="element-488">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-white" data-id="element-489">
                        {quoteResult.priceLow === quoteResult.priceHigh ? `${quoteResult.priceLow}` : `${quoteResult.priceLow}–${quoteResult.priceHigh}`}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center gap-3" data-id="element-490">
                    <a href="tel:206-619-3742" className="bg-white hover:bg-stone-200 text-stone-900 px-6 py-2.5 rounded-lg font-bold text-sm transition-colors" data-id="element-491">
                      Call to Order
                    </a>
                    <a href={`/?cat=${encodeURIComponent(material)}#materials`} onClick={e => {
                e.preventDefault();
                // Capture material before closing resets it
                const selectedMaterial = material;
                handleCloseEstimator();
                // Navigate to the materials section with the selected category expanded
                const params = new URLSearchParams({
                  cat: selectedMaterial
                });
                window.history.replaceState({}, '', `/?${params.toString()}`);
                window.dispatchEvent(new Event('materialsNav'));
              }} className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-colors" data-id="element-492">
                      Dig Deeper
                    </a>
                  </div>

                  {/* Delivery Zone Note */}
                  {quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-white/70 leading-relaxed text-center font-medium" data-id="element-493">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="text-brand-orange hover:underline" data-id="element-494">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-brand-orange" data-id="element-495">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-496" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="font-semibold ml-1 hover:underline" data-id="element-497">
                        check delivery zones
                      </Link>
                    </div>}

                  {/* Discreet reset */}
                  <div className="mt-4 flex justify-end" data-id="element-498">
                    <button onClick={() => {
                handleCloseEstimator();
                setShowEstimator(true);
                setTimeout(() => {
                  if (estimatorRef.current) {
                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                    const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight + 8;
                    window.scrollTo({
                      top,
                      behavior: 'smooth'
                    });
                  }
                }, 150);
              }} className="text-[10px] text-white/40 hover:text-white/70 transition-colors" data-id="element-499">
                      Start over ↻
                    </button>
                  </div>
                </motion.div>}
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.4
      }} className="flex flex-wrap justify-center md:justify-start gap-3 mt-8" data-id="element-500">
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-501">
            <CheckCircle2 className="w-4 h-4 mr-2 text-brand-orange" data-id="element-502" />
            Order from your phone in minutes
          </div>
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-503">
            <CheckCircle2 className="w-4 h-4 mr-2 text-brand-orange" data-id="element-504" />
            No hidden fees
          </div>
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-505">
            <CheckCircle2 className="w-4 h-4 mr-2 text-brand-orange" data-id="element-506" />
            Pay on delivery
          </div>
        </motion.div>
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-507" />
    </section>;
}